import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Clock, User, MapPin, Calendar } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PatientCombobox } from '@/components/custom/patient-combobox';
import { getLocationClient, getAppointmentTypeClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import type { ComputedSlot } from '@vagusx/clients';
import type { BookingPatientInfo } from '../types';

interface BookingDialogProps {
  open: boolean;
  slot: ComputedSlot | null;
  onClose: () => void;
  onBook: (data: {
    slot: ComputedSlot;
    patientId?: string;
    newPatient?: BookingPatientInfo;
    reasonForVisit?: string;
  }) => void;
  isBooking: boolean;
  canSearchPatients: boolean;
  /** 'find-slots': all slot fields read-only. 'calendar': location + appointment type editable */
  mode?: 'find-slots' | 'calendar';
  /** Pre-select this patient (e.g. scheduling from a referral). */
  initialPatient?: { id: string; label: string } | null;
}

const EMPTY_PATIENT: BookingPatientInfo = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  phone: '',
};

export function BookingDialog({
  open,
  slot,
  onClose,
  onBook,
  isBooking,
  canSearchPatients,
  mode = 'find-slots',
  initialPatient = null,
}: BookingDialogProps): React.ReactElement {
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [selectedPatientLabel, setSelectedPatientLabel] = useState('');
  // In-dialog patient creation was removed; `newPatient` + the onBook `newPatient`
  // channel and use-booking's createBookingPatient are kept so the create flow can
  // be re-enabled without re-plumbing the backend path.
  const [newPatient, setNewPatient] = useState<BookingPatientInfo>(EMPTY_PATIENT);
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Editable fields for calendar mode
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedAppointmentTypeId, setSelectedAppointmentTypeId] = useState('');

  // Fetch locations and appointment types for calendar mode
  const { data: locationsData } = useQuery({
    queryKey: queryKeys.locations.all,
    queryFn: () => getLocationClient().list(),
    enabled: mode === 'calendar' && open,
  });
  const locations = locationsData?.items ?? [];

  const { data: appointmentTypes } = useQuery({
    queryKey: queryKeys.appointmentTypes.all,
    queryFn: () => getAppointmentTypeClient().list(),
    enabled: mode === 'calendar' && open,
  });

  // Reset per-slot fields whenever the dialog opens or the selected slot changes.
  // The dialog stays mounted across slot clicks (open is driven by `slot !== null`),
  // so without this the previously chosen patient/reason would carry over to the
  // next slot. Re-apply the referral pre-select (if any) so that flow is preserved.
  useEffect(() => {
    if (!open || !slot) return;
    setSelectedLocationId(slot.locationId || '');
    setSelectedAppointmentTypeId(slot.appointmentTypeId || '');
    setReasonForVisit('');
    setErrors({});
    if (initialPatient) {
      setSelectedPatientId(initialPatient.id);
      setSelectedPatientLabel(initialPatient.label);
    } else {
      setSelectedPatientId(null);
      setSelectedPatientLabel('');
    }
  }, [open, slot, initialPatient]);

  // Resolve selected appointment type for duration
  const selectedType = appointmentTypes?.find((t) => t.id === selectedAppointmentTypeId);
  const resolvedDuration = selectedType?.durationMinutes ?? slot?.durationMinutes ?? 15;

  // Resolve display values
  const selectedLocation = locations.find((l) => l.id === selectedLocationId);

  // Build the final slot with overrides from editable fields
  const buildFinalSlot = (): ComputedSlot | null => {
    if (!slot) return null;
    if (mode === 'find-slots') return slot;

    return {
      ...slot,
      locationId: selectedLocationId,
      locationName: selectedLocation?.name ?? '',
      appointmentTypeId: selectedAppointmentTypeId,
      appointmentTypeName: selectedType?.name ?? '',
      durationMinutes: resolvedDuration,
    };
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    // Calendar mode: require location and appointment type
    if (mode === 'calendar') {
      if (!selectedLocationId) errs.locationId = 'Required';
      if (!selectedAppointmentTypeId) errs.appointmentTypeId = 'Required';
    }

    // Patient validation — a patient must be selected from search. In-dialog
    // patient creation was removed, so there's no new-patient fallback here.
    if (!selectedPatientId) {
      errs.patientId = 'Please select a patient';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleBook = (): void => {
    const finalSlot = buildFinalSlot();
    if (!finalSlot || !validate()) return;

    onBook({
      slot: finalSlot,
      patientId: selectedPatientId ?? undefined,
      newPatient: selectedPatientId ? undefined : newPatient,
      reasonForVisit: reasonForVisit.trim() || undefined,
    });
  };

  const handleClose = (): void => {
    setSelectedPatientId(null);
    setSelectedPatientLabel('');
    setNewPatient(EMPTY_PATIENT);
    setReasonForVisit('');
    setErrors({});
    setSelectedLocationId('');
    setSelectedAppointmentTypeId('');
    onClose();
  };

  if (!slot) return <></>;

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Book Appointment</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 shrink-0" />
                <span>{slot.displayTime}</span>
                <span className="text-muted-foreground">({resolvedDuration} min)</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 shrink-0" />
                <span>{slot.providerName || 'Select provider'}</span>
              </div>

              {/* Location — read-only or editable */}
              {mode === 'find-slots' ? (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span>{slot.locationName}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 shrink-0 mt-2" />
                  <div className="flex-1">
                    <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                      <SelectTrigger className={errors.locationId ? 'border-destructive' : ''}>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Appointment Type — read-only or editable */}
              {mode === 'find-slots' ? (
                slot.appointmentTypeName && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 shrink-0" />
                    <span>{slot.appointmentTypeName}</span>
                  </div>
                )
              ) : (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 shrink-0 mt-2" />
                  <div className="flex-1">
                    <Select
                      value={selectedAppointmentTypeId}
                      onValueChange={setSelectedAppointmentTypeId}
                    >
                      <SelectTrigger
                        className={errors.appointmentTypeId ? 'border-destructive' : ''}
                      >
                        <SelectValue placeholder="Select appointment type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(appointmentTypes ?? []).map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name} ({type.durationMinutes} min)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Separator />

        <div className="space-y-4">
          {/* Patient selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Patient</Label>

            {canSearchPatients && (
              <>
                <PatientCombobox
                  value={selectedPatientId}
                  onValueChange={(id, label) => {
                    setSelectedPatientId(id);
                    setSelectedPatientLabel(label);
                    setErrors({});
                  }}
                  initialLabel={selectedPatientLabel}
                  hasError={!!errors.patientId}
                />
                {errors.patientId && <p className="text-xs text-destructive">{errors.patientId}</p>}
              </>
            )}
          </div>

          {/* Note for the practice: stored as reasonForVisit and sent to the EHR as the
              booking note (athena `bookingnote`, ADR-291). */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              Note for the practice (optional)
            </Label>
            <Textarea
              value={reasonForVisit}
              onChange={(e) => setReasonForVisit(e.target.value)}
              placeholder="Sent to the EHR with the booking..."
              rows={2}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={isBooking}>
            Cancel
          </Button>
          <Button onClick={handleBook} disabled={isBooking}>
            {isBooking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Book Appointment
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
